import { useEffect, useState } from "react";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { CACHE_MAX_AGE, CACHE_STALE_TIME, createAccountPersistence } from "../utils/queryPersistence.mjs";
import { useAuth } from "./AuthContext";
import { createIndexedDBStorage } from "../utils/indexedDBStorage.mjs";

function AccountQueries({ children, userId }) {
  const [client] = useState(() => new QueryClient({ defaultOptions: {
    queries: { staleTime: CACHE_STALE_TIME, gcTime: CACHE_MAX_AGE, retry: (count, error) => ![401, 403, 404].includes(error.status) && count < 1 },
    mutations: { retry: false },
  } }));
  const [persistence] = useState(() => {
    let factory;
    const legacy = [];
    try { factory = window.indexedDB; } catch { /* Browser storage may be blocked. */ }
    for (const name of ["localStorage", "sessionStorage"]) {
      try { legacy.push(window[name]); } catch { /* Migration is optional. */ }
    }
    return createAccountPersistence(userId, createIndexedDBStorage(factory), legacy);
  });
  useEffect(() => () => {
    persistence.dispose();
    client.clear();
  }, [client, persistence]);
  return <PersistQueryClientProvider client={client} persistOptions={persistence.options}>{children}</PersistQueryClientProvider>;
}

export default function QueryProvider({ children }) {
  const { userId } = useAuth();
  // Replacing the provider also isolates late responses from a previous account.
  return <AccountQueries key={userId || "anonymous"} userId={userId}>{children}</AccountQueries>;
}
